<script lang="ts" setup>
import { ref } from "vue"
import { useRoute } from "vue-router"
import axios from "axios"
import { startAuthentication } from "@simplewebauthn/browser"

const route = useRoute()
const email = ref("")
const password = ref("")
const error = ref("")
const loading = ref(false)
const passkeyLoading = ref(false)

function redirectWithCode(code: string) {
    const redirectUri = route.query.redirect_uri as string || "/"
    window.location.href = `${redirectUri}${redirectUri.includes("?") ? "&" : "?"}code=${code}`
}

async function handleLogin() {
    error.value = ""
    loading.value = true
    try {
        const res = await axios.post("/testup-auth/auth/login", {
            email: email.value,
            password: password.value
        })
        redirectWithCode(res.data.code)
    } catch (e: any) {
        error.value = e.response?.data?.error || "Login failed"
    } finally {
        loading.value = false
    }
}

async function handlePasskeyLogin() {
    error.value = ""
    passkeyLoading.value = true
    try {
        const optionsRes = await axios.post("/testup-auth/auth/passkey/login/options", {
            email: email.value || undefined
        })
        const authResponse = await startAuthentication(optionsRes.data)
        const verifyRes = await axios.post("/testup-auth/auth/passkey/login/verify", {
            email: email.value || undefined,
            response: authResponse
        })
        redirectWithCode(verifyRes.data.code)
    } catch (e: any) {
        if (e.name === "NotAllowedError") {
            error.value = "Passkey authentication was cancelled"
        } else {
            error.value = e.response?.data?.error || e.message || "Passkey login failed"
        }
    } finally {
        passkeyLoading.value = false
    }
}
</script>

<template lang="pug">
.auth-page
    .auth-card
        img.auth-logo(src="@/assets/images/testup-icon-rahmenlos-300x220.png")
        h2 Login
        .auth-error(v-if="error") {{ error }}
        form(@submit.prevent="handleLogin")
            .form-group
                label(for="email") Email
                input#email(
                    v-model="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                    autocomplete="email"
                )
            .form-group
                label(for="password") Password
                input#password(
                    v-model="password"
                    type="password"
                    placeholder="Password"
                    required
                    autocomplete="current-password"
                )
            button.auth-btn(type="submit" :disabled="loading || passkeyLoading")
                | {{ loading ? "Logging in..." : "Login" }}
        .auth-divider
            span or
        button.auth-btn.auth-btn-passkey(
            @click="handlePasskeyLogin"
            :disabled="loading || passkeyLoading"
        )
            i.fas.fa-key
            | &nbsp;{{ passkeyLoading ? "Authenticating..." : "Sign in with Passkey" }}
        .auth-footer
            | Don't have an account?&nbsp;
            router-link(to="/signup") Sign up
</template>

<style scoped>
.auth-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(160deg, var(--c-bg) 0%, var(--c-bg-soft) 100%);
}

.auth-card {
    background: white;
    padding: 48px 40px;
    border-radius: var(--r-card);
    box-shadow: var(--shadow-card-hover);
    max-width: 400px;
    width: 100%;
    text-align: center;
}

.auth-logo {
    width: 60px;
    margin-bottom: 16px;
}

h2 {
    margin: 0 0 24px 0;
    color: var(--black);
}

.auth-error {
    background: #fef2f2;
    color: var(--red);
    padding: 10px 14px;
    border-radius: var(--r-btn);
    margin-bottom: 16px;
    font-size: 14px;
}

.form-group {
    margin-bottom: 16px;
    text-align: left;
}

.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.form-group input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--light-grey);
    border-radius: var(--r-btn);
    font-size: 14px;
    box-sizing: border-box;
}

.form-group input:focus {
    outline: none;
    border-color: var(--green);
    box-shadow: 0 0 0 2px rgba(17, 140, 24, 0.15);
}

.auth-btn {
    width: 100%;
    padding: 12px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: var(--r-btn);
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.auth-btn:hover:not(:disabled) {
    background: var(--dark-green);
}

.auth-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.auth-divider {
    margin: 20px 0;
    text-align: center;
    color: var(--dark-grey);
    font-size: 13px;
    position: relative;
}

.auth-divider::before,
.auth-divider::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 40%;
    height: 1px;
    background: var(--light-grey);
}

.auth-divider::before { left: 0; }
.auth-divider::after { right: 0; }

.auth-btn-passkey {
    background: white;
    color: #333;
    border: 1px solid var(--light-grey);
}

.auth-btn-passkey:hover:not(:disabled) {
    background: var(--very-light-grey);
}

.auth-footer {
    margin-top: 20px;
    font-size: 14px;
    color: var(--dark-grey);
}

.auth-footer a {
    color: var(--green);
    text-decoration: none;
    font-weight: 500;
}
</style>
